import { MessageSquare, Plus, Trash2, X, Loader2 } from 'lucide-react';
import { useChatConversations, useDeleteConversation } from '../../hooks/useChatMessages';
import { useChatContext } from '../../contexts/ChatContext';
import { useState } from 'react';

export default function ChatHistorySidebar() {
  const { setConversationId, setShowHistory, conversationId } = useChatContext();
  const { data: conversations, isLoading } = useChatConversations();
  const deleteConversation = useDeleteConversation();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleSelect = (id: string) => {
    setConversationId(id);
    setShowHistory(false);
  };

  const handleNewChat = () => {
    setConversationId(null);
    setShowHistory(false);
  };

  const handleDelete = (id: string) => {
    if (confirmDelete === id) {
      deleteConversation.mutate(id, {
        onSuccess: () => {
          if (conversationId === id) setConversationId(null);
          setConfirmDelete(null);
        },
      });
    } else {
      setConfirmDelete(id);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  return (
    <div className="absolute inset-0 bg-white dark:bg-zinc-800 z-10 flex flex-col">
      <div className="flex items-center justify-between p-3 border-b border-theme-border">
        <h3 className="font-medium text-sm text-theme-primary">Gesprekken</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={handleNewChat}
            className="p-1.5 text-theme-secondary hover:text-brand transition-colors rounded-lg hover:bg-theme-surface"
            title="Nieuw gesprek"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowHistory(false)}
            className="p-1.5 text-theme-secondary hover:text-theme-primary transition-colors rounded-lg hover:bg-theme-surface"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-theme-tertiary" />
          </div>
        ) : !conversations || conversations.length === 0 ? (
          <div className="text-center py-8 text-theme-tertiary text-sm">
            Nog geen gesprekken
          </div>
        ) : (
          <div className="py-1">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-theme-surface transition-colors ${
                  conv.id === conversationId ? 'bg-theme-surface' : ''
                }`}
              >
                <button
                  onClick={() => handleSelect(conv.id)}
                  className="flex-1 flex items-start gap-2 min-w-0 text-left"
                >
                  <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-theme-tertiary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-theme-primary truncate">
                      {conv.title || 'Nieuw gesprek'}
                    </p>
                    <p className="text-[10px] text-theme-tertiary">
                      {new Date(conv.updated_at).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(conv.id); }}
                  className={`flex-shrink-0 p-1 rounded transition-colors ${
                    confirmDelete === conv.id
                      ? 'text-red-500 bg-red-50 dark:bg-red-950'
                      : 'text-theme-tertiary opacity-0 group-hover:opacity-100 hover:text-red-500'
                  }`}
                  title={confirmDelete === conv.id ? 'Nogmaals klikken om te verwijderen' : 'Verwijderen'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
